import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import type { GuestContract } from "@/types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface ContractGateProps {
  propertyName?: string;
  contract: GuestContract;
  accepting?: boolean;
  onAccept: () => void;
  onSkip?: () => void;
  /** 체결본 파일 URL — 체결 후 열람일 때 "내려받기" 를 띄운다. */
  documentUrl?: string;
  /** 체결 후 열람에서 챗으로 돌아가는 길. 없으면 체결 전 게이트로 동작한다. */
  onClose?: () => void;
}

/**
 * Full-screen contract gate shown after language selection, before the chat.
 *
 * 체결 뒤에도 같은 화면이 **열람(교부)** 모드로 다시 열린다 (`onClose` 가 있고
 * `status === "accepted"`) — 이때 본문은 계약서 원본이 아니라 체결 스냅샷이다.
 */
export function ContractGate({
  propertyName,
  contract,
  accepting,
  onAccept,
  onSkip,
  documentUrl,
  onClose,
}: ContractGateProps) {
  const stay = contract.stay;
  const viewing = contract.status === "accepted" && Boolean(onClose);
  return (
    <div className="bg-atmosphere grain flex h-dvh flex-col items-center px-4 py-6">
      <div className="relative z-10 flex w-full max-w-2xl flex-1 flex-col gap-4 overflow-hidden">
        <div className="flex flex-col items-center gap-2 text-center">
          <Logo size={40} />
          {propertyName ? (
            <p className="font-display text-lg tracking-tight text-foreground">{propertyName}</p>
          ) : null}
          <p className="text-sm text-muted-foreground">
            {viewing
              ? "숙박 계약 체결본 · Signed agreement"
              : "숙박 계약서 · Accommodation Agreement"}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
          {stay ? (
            <dl className="mb-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
              {stay.guest_name ? (
                <>
                  <dt>게스트 · Guest</dt>
                  <dd className="text-foreground">{stay.guest_name}</dd>
                </>
              ) : null}
              {stay.room_type_name ? (
                <>
                  <dt>객실 타입 · Room type</dt>
                  <dd className="text-foreground">{stay.room_type_name}</dd>
                </>
              ) : null}
              <dt>호실 · Room</dt>
              <dd className="text-foreground">{stay.room_number ?? "미배정 (TBD)"}</dd>
              {stay.check_in ? (
                <>
                  <dt>체크인 · Check-in</dt>
                  <dd className="text-foreground">{formatDate(stay.check_in)}</dd>
                </>
              ) : null}
              {stay.check_out ? (
                <>
                  <dt>체크아웃 · Check-out</dt>
                  <dd className="text-foreground">{formatDate(stay.check_out)}</dd>
                </>
              ) : null}
              {viewing && contract.accepted_at ? (
                <>
                  <dt>동의 시각 · Agreed at</dt>
                  <dd className="text-foreground tabular-nums">
                    {new Date(contract.accepted_at).toLocaleString()}
                  </dd>
                </>
              ) : null}
            </dl>
          ) : null}
          <div className="chat-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{contract.body ?? ""}</ReactMarkdown>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {viewing ? (
            <>
              {documentUrl ? (
                <Button asChild className="h-12 rounded-xl text-base">
                  <a href={documentUrl} download>
                    체결본 내려받기 · Download copy
                  </a>
                </Button>
              ) : null}
              <Button variant="ghost" className="rounded-xl text-muted-foreground" onClick={onClose}>
                닫기 · Close
              </Button>
            </>
          ) : (
            <Button className="h-12 rounded-xl text-base" disabled={accepting} onClick={onAccept}>
              동의 및 확인 · Agree & Continue
            </Button>
          )}
          {!viewing && !contract.required && onSkip ? (
            <Button
              variant="ghost"
              className="rounded-xl text-muted-foreground"
              disabled={accepting}
              onClick={onSkip}
            >
              나중에 하기 · Later
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
